import * as types from '../mutation-types'
import req from '../../lib/authRequest'

/*
 * Vuex module to handle Server related data

    Server model:
      - id
      - name
      - status
      - ip_address
      - mem
      - cpu
      - managed
 */

const state = {
  allServers: [],
  allServersCount: 0,
  servers: [],
  // If the timers in the StatusButton component have been loaded
  refresh_timers_loaded: false,
  database_timers_loaded: false,
  vhost_timers_loaded: false,
  new_databases: [],
  new_vhosts: [],
  ssl_certs: [],
  databases: [],
  editing_db_id: -1,
  editing_db_name: '',
  vhosts: [],
  ftp_users: [],
  new_ftp_user_vhost_id: -1,
  editing_ftp_id: -1,
  editing_ftp_username: '',
  update_ftp_user_vhost_id: -1,
  update_vhost_ftp_open: false,
  domain_redirects: [],
  php_settings: [],
  nginx_status: '',
  // Viewing the details of this server
  viewing_server_iid: -1,
  // Temporary object used before submit to update server
  editing_server: {},
  cpu_min: 1,
  cpu_max: 8,
  cpu_cost_each: 8,
  memory_min: 1,
  memory_max: 16,
  memory_cost_each: 15,
  storage_min: 8,
  storage_max: 1024,
  storage_cost_each: 0.5,
  // If a request to create a server is currently happening
  creating_server: false,
  // If a request to update a server is currently happening
  updating_server: false,
  deleting_server: false
}

const getters = {
  serversCount: state => state.servers.length,
  hasServers: (state, getters) => getters.serversCount > 0,
  viewingServer: state => {
    if (state.servers.length === 0) {
      return {
        cpus: 1,
        memory: 1024,
        disk: 10
      }
    }
    return state.servers.find(server => {
      return server.iid.toString() === state.viewing_server_iid.toString()
    })
  },
  sslCertsCount: state => state.ssl_certs.length,
  hasSslCerts: (state, getters) => getters.sslCertsCount > 0,
  ftpUsersCount: state => state.ftp_users.length,
  domainRedirectsCount: state => state.domain_redirects.length,
  newDatabaseById: (state) => (id) => {
    return state.new_databases.find(database => database.database_id === id)
  },
  newDatabaseButtonText: (state, getters) => (id) => {
    let db = getters.newDatabaseById(id)
    if (db === undefined) {
      return 'Delete'
    } else {
      return 'Delete (Disabled for ' + db.last_start.toString() + 's)'
    }
  },
  newVhostById: (state) => (id) => {
    return state.new_vhosts.find(vhost => vhost.vhost_id === id)
  },
  newVhostDeleteText: (state, getters) => (id) => {
    let vhost = getters.newVhostById(id)
    if (vhost === undefined) {
      return 'Delete'
    } else {
      return 'Delete (Disabled for ' + vhost.last_start.toString() + 's)'
    }
  },
  vhostsAlphabetical: state => {
    let vhostsAlphabetical = JSON.parse(JSON.stringify(state.vhosts))
    vhostsAlphabetical.sort((a, b) => {
      return a.vhost.localeCompare(b.vhost)
    })
    return vhostsAlphabetical
  }
}

const mutations = {
  [types.SET_SERVERS] (state, servers) {
    state.servers = servers
  },
  [types.ADD_SERVER] (state, server) {
    state.servers.push(server)
  },
  [types.SET_SERVER] (state, { iid, server }) {
    let pos = state.servers.map(server => server.iid).indexOf(iid)
    if (pos !== -1) {
      state.servers[pos] = server
    } else {
      state.servers.push(server)
    }
  },
  [types.SET_SERVER_STATUS] (state, { iid, power, time }) {
    let pos = state.servers.map(server => server.iid).indexOf(iid)
    if (pos !== -1) {
      state.servers[pos]['power'] = power
      state.servers[pos]['time_since_shutdown_issued'] = time
    }
  },
  [types.INCREASE_LAST_SHUTDOWN] (state, iid) {
    let pos = state.servers.map(server => server.iid).indexOf(iid)
    if (pos !== -1) {
      state.servers[pos]['time_since_shutdown_issued'] += 1
    }
  },
  [types.SET_VIEWING_SERVER_IID] (state, serverIid) {
    state.viewing_server_iid = serverIid
  },
  [types.SET_EDITING_SERVER] (state, server) {
    state.editing_server = JSON.parse(JSON.stringify(server))
    // Convert memory from gb to mb
    state.editing_server.mem_gb = state.editing_server.mem / 1024
  },
  [types.UPDATE_EDITING_SERVER] (state, { name, value }) {
    state.editing_server[name] = value
  },
  [types.SET_SSL_CERTS] (state, sslCerts) {
    state.ssl_certs = sslCerts
  },
  [types.SET_DATABASES] (state, databases) {
    state.databases = databases
  },
  [types.SET_EDITING_DB] (state, { databaseName, databaseId }) {
    state.editing_db_name = databaseName
    state.editing_db_id = databaseId
  },
  [types.SET_VHOSTS] (state, vhosts) {
    state.vhosts = vhosts
  },
  [types.SET_FTP_USERS] (state, ftpUsers) {
    state.ftp_users = ftpUsers
  },
  [types.SET_NEW_FTP_VHOST_ID] (state, vhostId) {
    state.new_ftp_user_vhost_id = vhostId
  },
  [types.SET_UPDATE_FTP] (state, { vhostId, ftpId, ftpUsername }) {
    state.update_ftp_user_vhost_id = vhostId
    state.editing_ftp_id = ftpId
    state.editing_ftp_username = ftpUsername
  },
  [types.SET_DOMAIN_REDIRECTS] (state, domainRedirects) {
    state.domain_redirects = domainRedirects
  },
  [types.SET_PHP_SETTINGS] (state, phpSettings) {
    state.php_settings = phpSettings
  },
  [types.SET_NGINX_STATUS] (state, nginxStatus) {
    state.nginx_status = nginxStatus
  },
  [types.SERVER_REFRESH_TIMERS_LOADED] (state) {
    state.refresh_timers_loaded = true
  },
  [types.RESET_SERVER_REFRESH_TIMERS] (state) {
    state.refresh_timers_loaded = false
  },
  [types.DATABASE_REFRESH_TIMERS_LOADED] (state) {
    state.database_timers_loaded = true
    state.new_databases = []
  },
  [types.RESET_DATABASE_TIMERS] (state) {
    state.database_timers_loaded = false
    state.new_databases = []
  },
  [types.NEW_DATABASE_CREATED] (state, database) {
    database.last_start = 60
    state.new_databases.push(database)
  },
  [types.INCREASE_STARTUP_NEW_DATABASE] (state, index) {
    let db = state.new_databases[index]
    db.last_start -= 1
    state.new_databases[index] = db
  },
  [types.SET_DATABASE_BY_INDEX] (state, { database, index }) {
    state.new_databases[index] = database
  },
  [types.REMOVE_DATABASE_BY_INDEX] (state, index) {
    state.new_databases.splice(index, 1)
  },
  [types.VHOST_REFRESH_TIMERS_LOADED] (state) {
    state.vhost_timers_loaded = true
    state.new_vhosts = []
  },
  [types.RESET_VHOST_TIMERS] (state) {
    state.vhost_timers_loaded = false
    state.new_vhosts = []
  },
  [types.NEW_VHOST_CREATED] (state, vhost) {
    vhost.last_start = 60
    state.new_vhosts.push(vhost)
  },
  [types.INCREASE_STARTUP_NEW_VHOST] (state, index) {
    let vhost = state.new_vhosts[index]
    vhost.last_start -= 1
    state.new_vhosts[index] = vhost
  },
  [types.SET_NEW_VHOST_BY_INDEX] (state, { vhost, index }) {
    state.new_vhosts[index] = vhost
  },
  [types.REMOVE_NEW_VHOST_BY_INDEX] (state, index) {
    state.new_vhosts.splice(index, 1)
  },
  [types.CREATING_SERVER] (state) {
    state.creating_server = true
  },
  [types.FINISHED_CREATING_SERVER] (state) {
    state.creating_server = false
  },
  [types.UPDATING_SERVER] (state) {
    state.updating_server = true
  },
  [types.FINISHED_UPDATING_SERVER] (state) {
    state.updating_server = false
  },
  [types.DELETING_SERVER] (state) {
    state.deleting_server = true
  },
  [types.FINISHED_DELETING_SERVER] (state) {
    state.deleting_server = false
  },
  [types.SET_ALL_SERVERS] (state, servers) {
    state.allServers = servers
  },
  [types.SET_ALL_SERVERS_COUNT] (state, count) {
    state.allServersCount = count
  }
}

const actions = {
  getAllServers ({ commit }, { sortedColumn, sortUp }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/servers/all/' + sortedColumn + '/' + sortUp,
        success (result) {
          commit(types.SET_ALL_SERVERS, result)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getAllServersCount ({ commit }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/servers/all/count',
        success (result) {
          commit(types.SET_ALL_SERVERS_COUNT, result.count)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getServers ({ commit }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/servers',
        success (result) {
          commit(types.SET_SERVERS, result)
          resolve()
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getServerStatus ({ commit }, iid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/servers/' + iid + '/power_status',
        success (result) {
          commit(types.SET_SERVER_STATUS, {
            iid: iid,
            power: result.power,
            time: result.time_since_shutdown_issued })
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getServer ({ commit, state }, iid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/servers/' + iid,
        success (result) {
          commit(types.SET_SERVER, { iid: iid, server: result })
          resolve()
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  createServer ({ commit }, newServer) {
    return new Promise((resolve, reject) => {
      commit(types.CREATING_SERVER)
      req.post({
        url: '/servers/',
        data: newServer,
        notifications: false,
        modalNotifications: true,
        success (result) {
          commit(types.SET_SERVER, {
            iid: newServer.iid,
            server: result.server
          })
          commit(types.FINISHED_CREATING_SERVER)
          resolve(result)
        },
        error (result) {
          commit(types.FINISHED_CREATING_SERVER)
          reject(result)
        }
      })
    })
  },

  updateServer ({ commit, dispatch }, editingServer) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_SERVER)
      // Replace gb with mb conversion
      editingServer.mem = editingServer.mem_gb * 1024
      let putData = {
        cpus: Number(editingServer.cpus),
        mem: Number(editingServer.mem),
        disk: Number(editingServer.disk)
      }
      req.put({
        url: '/servers/' + editingServer.iid,
        data: putData,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getServers')
          commit(types.FINISHED_UPDATING_SERVER)
          resolve(result)
        },
        error (result) {
          commit(types.FINISHED_UPDATING_SERVER)
          reject(result)
        }
      })
    })
  },

  deleteServer ({ commit }, serverIid) {
    return new Promise((resolve, reject) => {
      commit(types.DELETING_SERVER)
      req.delete({
        url: '/servers/' + serverIid,
        success (result) {
          commit(types.FINISHED_DELETING_SERVER)
          resolve(result)
        },
        error (result) {
          commit(types.FINISHED_DELETING_SERVER)
          reject(result)
        }
      })
    })
  },

  startServer ({ dispatch, commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.post({
        url: '/servers/' + serverIid + '/start',
        success (result) {
          dispatch('getServer', serverIid)
          resolve()
        },
        error (result) {
          reject()
        }
      })
    })
  },

  stopServer ({ dispatch, commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.post({
        url: '/servers/' + serverIid + '/stop',
        success (result) {
          dispatch('getServer', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  forceStopServer ({ dispatch, commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.post({
        url: '/servers/' + serverIid + '/force_stop',
        success (result) {
          dispatch('getServer', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  setNewStatus ({ commit }, { serverIid, newStatus }) {
    return new Promise((resolve, reject) => {
      req.post({
        url: '/servers/' + serverIid + '/' + newStatus,
        success (result) {
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  /**
    Set the server that is currently being viewed and fetch it's attributes
  */
  setViewingServer ({ commit, dispatch, getters, state }, serverIid) {
    return new Promise((resolve, reject) => {
      if (!serverIid) {
        return
      }
      commit(types.SET_VIEWING_SERVER_IID, serverIid)
      dispatch('getServer', serverIid).then(response => {
        if (getters.viewingServer === undefined) {
          commit(types.ADD_NOTIFICATION, {
            type: 'warning',
            message: 'Server not found'
          })
          reject()
        } else if (getters.viewingServer.managed) {
          dispatch('getManagedServer', serverIid).then(response => {
            resolve()
          })
        }
      })
    })
  },

  // Get all of the managed attributes of a server
  getManagedServer ({ dispatch }, serverIid) {
    return new Promise((resolve, reject) => {
      dispatch('getSslCerts', serverIid)
      dispatch('getDatabases', serverIid)
      dispatch('getVhosts', serverIid)
      dispatch('getFtpUsers', serverIid)
      dispatch('getDomainRedirects', serverIid)
      dispatch('getPhpSettings', serverIid)
      // TODO enable once working
      dispatch('getNginxStatus', serverIid)
      resolve()
    })
  },

  getSslCerts ({ commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/managed_vm/' + serverIid + '/ssl_certs',
        success (result) {
          commit(types.SET_SSL_CERTS, result)
          resolve()
        },
        error () {
          reject()
        }
      })
    })
  },

  createSSLCert ({ dispatch }, { serverIid, name, cert, key, ca }) {
    return new Promise((resolve, reject) => {
      let sslCert = {
        name: name,
        cert: cert,
        key: key,
        ca: ca
      }
      req.post({
        url: '/managed_vm/' + serverIid + '/ssl_certs',
        data: sslCert,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getSslCerts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  deleteSSLCert ({ dispatch }, { serverIid, certId }) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/managed_vm/' + serverIid + '/ssl_certs/' + certId,
        success (result) {
          dispatch('getSslCerts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getDatabases ({ commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/managed_vm/' + serverIid + '/mysql_database',
        success (result) {
          commit(types.SET_DATABASES, result)
          resolve()
        },
        error () {
          reject()
        }
      })
    })
  },
  // eslint-disable-next-line camelcase
  createDatabase ({ commit, dispatch }, { serverIid, name, db_username, db_password }) {
    return new Promise((resolve, reject) => {
      let database = {
        database_name: name,
        username: db_username,
        password: db_password
      }
      req.post({
        url: '/managed_vm/' + serverIid + '/mysql_database',
        data: database,
        notifications: false,
        modalNotifications: true,
        success (result) {
          commit(types.NEW_DATABASE_CREATED, result)
          dispatch('getDatabases', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  updateDbPass ({ dispatch }, { serverIid, databaseId, password }) {
    return new Promise((resolve, reject) => {
      let putData = { password: password }
      req.put({
        url: '/managed_vm/' + serverIid + '/mysql_database/' + databaseId,
        data: putData,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getDatabases', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getVhosts ({ commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/managed_vm/' + serverIid + '/vhosts',
        success (result) {
          commit(types.SET_VHOSTS, result)
          resolve()
        },
        error () {
          reject()
        }
      })
    })
  },
  // eslint-disable-next-line camelcase
  createVhost ({ commit, dispatch }, { serverIid, domain, aliases, ssl_enabled, ssl_id, ssl_only }) {
    return new Promise((resolve, reject) => {
      let vhost = {
        domain: domain,
        aliases: aliases.filter(function (n) { return n !== '' }),
        ssl_enabled: ssl_enabled,
        ssl_id: ssl_id,
        ssl_only: ssl_only
      }
      req.post({
        url: '/managed_vm/' + serverIid + '/vhosts',
        data: vhost,
        notifications: false,
        modalNotifications: true,
        success (result) {
          commit(types.NEW_VHOST_CREATED, result)
          dispatch('getVhosts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  deleteVhost ({ dispatch }, { serverIid, vhostId }) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/managed_vm/' + serverIid + '/vhosts/' + vhostId,
        success (result) {
          dispatch('getVhosts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  updateVhostAliases ({ dispatch }, { serverIid, vhostId, aliases }) {
    return new Promise((resolve, reject) => {
      let putData = {
        aliases: aliases.filter(function (n) { return n !== '' })
      }
      req.put({
        url: '/managed_vm/' + serverIid + '/vhosts/' + vhostId + '/domain_aliases',
        data: putData,
        success (result) {
          dispatch('getVhosts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  updateVhostFtp ({ dispatch }, { serverIid, ftpId, password }) {
    return new Promise((resolve, reject) => {
      let postData = {
        password: password
      }
      req.put({
        url: '/managed_vm/' + serverIid + '/ftp_users/' + ftpId,
        data: postData,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getVhosts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  addVhostFtp ({ dispatch }, { serverIid, username, password, vhostId }) {
    return new Promise((resolve, reject) => {
      let postData = {
        username: username,
        password: password,
        vhost_id: vhostId
      }
      req.post({
        url: '/managed_vm/' + serverIid + '/ftp_users',
        data: postData,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getVhosts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  deleteFtpUser ({ dispatch }, { serverIid, ftpId }) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/managed_vm/' + serverIid + '/ftp_users/' + ftpId,
        success (result) {
          dispatch('getVhosts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  updateVhostHttps ({ dispatch }, { serverIid, vhostId, sslOnly, sslEnabled, sslId }) {
    return new Promise((resolve, reject) => {
      let postData = {
        ssl_only: sslOnly,
        ssl_enabled: sslEnabled,
        ssl_id: sslId
      }
      req.put({
        url: '/managed_vm/' + serverIid + '/vhosts/' + vhostId,
        data: postData,
        success (result) {
          dispatch('getVhosts', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getFtpUsers ({ commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/managed_vm/' + serverIid + '/ftp_users',
        success (result) {
          commit(types.SET_FTP_USERS, result)
          resolve()
        },
        error () {
          reject()
        }
      })
    })
  },

  getDomainRedirects ({ commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/managed_vm/' + serverIid + '/domain_redirects',
        success (result) {
          commit(types.SET_DOMAIN_REDIRECTS, result)
          resolve()
        },
        error () {
          reject()
        }
      })
    })
  },
  // eslint-disable-next-line camelcase
  createDomainRedirect ({ dispatch }, { serverIid, redirect_from, redirect_to }) {
    return new Promise((resolve, reject) => {
      let redirect = {
        redirect_from: redirect_from,
        redirect_to: redirect_to
      }
      req.post({
        url: '/managed_vm/' + serverIid + '/domain_redirects',
        data: redirect,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getDomainRedirects', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  deleteDomainRedirect ({ dispatch }, { serverIid, redirectId }) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/managed_vm/' + serverIid + '/domain_redirects/' + redirectId,
        success (result) {
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getPhpSettings ({ commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/managed_vm/' + serverIid + '/php_settings',
        success (result) {
          commit(types.SET_PHP_SETTINGS, result)
          resolve()
        },
        error () {
          reject()
        }
      })
    })
  },

  createPhpSetting ({ dispatch }, { serverIid, key, value }) {
    return new Promise((resolve, reject) => {
      let phpSetting = {
        key: key,
        value: value
      }
      req.post({
        url: '/managed_vm/' + serverIid + '/php_settings',
        data: phpSetting,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getPhpSettings', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  deletePhpSetting ({ commit, dispatch }, { serverIid, phpKey }) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/managed_vm/' + serverIid + '/php_settings/' + phpKey,
        success (result) {
          dispatch('getPhpSettings', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  deleteDatabase ({ commit, dispatch }, { serverIid, databaseId }) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/managed_vm/' + serverIid + '/mysql_database/' + databaseId,
        success (result) {
          dispatch('getDatabases', serverIid)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  getNginxStatus ({ commit }, serverIid) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/managed_vm/' + serverIid + '/nginx',
        success (result) {
          commit(types.SET_NGINX_STATUS, result.status)
          resolve()
        },
        error () {
          reject()
        }
      })
    })
  },

  toggleNginxStatus ({ state, dispatch }, serverIid) {
    return new Promise((resolve, reject) => {
      req.put({
        url: '/managed_vm/' + serverIid + '/nginx',
        data: {
          enable: !state.nginx_status
        },
        success (result) {
          dispatch('getNginxStatus', serverIid)
          resolve()
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  changeWebsitesPassword ({ commit }, { serverIid, password }) {
    return new Promise((resolve, reject) => {
      req.put({
        url: '/managed_vm/' + serverIid + '/websites_user',
        data: {
          password: password
        },
        notifications: false,
        modalNotifications: true,
        success (result) {
          commit(types.ADD_NOTIFICATION_FROM_RESULT, result)
          resolve(result)
        },
        error () {
          reject()
        }
      })
    })
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/servers.js